import type { Router } from 'vue-router'
import * as Sentry from '@sentry/vue'
import { i18n } from '../i18n'

// 剛重整過又立刻失敗代表重整救不回來，用時間戳擋掉重整迴圈（不永久停用，分頁開很久也還能自救）
// 只擋「重整」這個動作，回報一律照送，否則最需要診斷資料的情境反而拿不到資料
const LAST_RELOAD_KEY = 'chunk-load-failure-reloaded-at'
const RELOAD_GUARD_MS = 10_000

// 探測流程進行中：由它決定要自動重整還是提示，期間 router.onError 不重複打擾使用者
let handling = false

// 同一支檔案可能同時觸發 resource error 與 preloadError，只診斷一次
const diagnosed = new Set<string>()

type ProbeResult =
  | { status: number; contentType: string | null; size: number; body?: string }
  | { error: string }

/** 內容型別是不是真的程式碼。被污染的快取條目是一份完整 HTML，狀態碼一樣是 200，只看狀態碼會誤判 */
function isCode(contentType: string | null): boolean {
  return !!contentType && /javascript|css/.test(contentType)
}

/**
 * 實際抓一次目標檔案，回傳結果供判斷失敗原因
 * 請求模式對齊 module script（modulepreload 是 crossorigin anonymous），
 * 確保 cache: 'reload' 覆蓋到的是 module 真正在用的那份快取條目
 */
async function probe(url: string, cache: RequestCache): Promise<ProbeResult> {
  try {
    const res = await fetch(url, { cache, mode: 'cors', credentials: 'same-origin' })
    const contentType = res.headers.get('content-type')
    const text = await res.text()
    return {
      status: res.status,
      contentType,
      size: text.length,
      // 拿到的不是程式碼時記下開頭，用來分辨是 index.html、404 頁還是其他東西
      body: isCode(contentType) ? undefined : text.slice(0, 200),
    }
  } catch (error) {
    return { error: String(error) }
  }
}

/**
 * chunk 載入失敗時重抓該檔案並回報 Sentry，用「走快取」與「強制重抓」兩次結果分辨原因：
 * 快取壞掉（前者不是程式碼、後者是）／被擋掉（都失敗）／舊版分頁（都 404）／瞬斷（都成功）。
 * 強制重抓會順便覆蓋掉壞掉的快取，所以只要重抓得到正確內容就重整一次讓使用者恢復正常。
 */
async function diagnoseAndRecover(url: string): Promise<void> {
  if (diagnosed.has(url)) return
  diagnosed.add(url)
  handling = true

  const cached = await probe(url, 'default')
  const fresh = await probe(url, 'reload')

  Sentry.captureMessage('Chunk load failure', {
    level: 'error',
    extra: { url, cached, fresh, online: navigator.onLine, page: window.location.pathname },
  })

  const justReloaded = Date.now() - Number(sessionStorage.getItem(LAST_RELOAD_KEY) || 0) < RELOAD_GUARD_MS
  const recovered = 'status' in fresh && fresh.status === 200 && isCode(fresh.contentType)

  if (recovered && !justReloaded) {
    sessionStorage.setItem(LAST_RELOAD_KEY, String(Date.now()))
    await Sentry.flush(2000)
    window.location.reload()
    return
  }

  // 重抓也拿不到、或剛重整過還是失敗，重整沒有意義，改為告知使用者
  handling = false
  alert(i18n.global.t('common.pageLoadFailed'))
}

/**
 * 接手處理頁面檔案載入失敗，兩個來源缺一不可：
 *
 * 1. resource error：Vite 對非 CSS 的 modulepreload 沒有掛 error listener，失敗時完全靜默，
 *    而瀏覽器印出的 MIME 錯誤不走 console API，Sentry 也攔不到。resource 錯誤不會冒泡，
 *    只能在 capture 階段攔截。這是唯一取得「真正壞掉的那支相依檔案」的途徑。
 * 2. vite:preloadError：官方事件，但 payload 只帶得出被動態 import 的那支檔案，
 *    指不出真正的元兇。不呼叫 preventDefault()，讓原始錯誤照常拋出給 Sentry。
 *    https://vite.dev/guide/build#load-error-handling
 */
export function registerChunkLoadRecovery(): void {
  window.addEventListener('error', (event) => {
    const target = event.target
    if (!(target instanceof HTMLLinkElement) && !(target instanceof HTMLScriptElement)) return

    const url = target instanceof HTMLLinkElement ? target.href : target.src
    if (!url.includes('/assets/')) return

    void diagnoseAndRecover(url)
  }, true)

  window.addEventListener('vite:preloadError', (event) => {
    // 失敗檔案的網址：JS 是絕對網址、CSS 是 /assets/ 開頭的路徑
    const message = (event as Event & { payload?: Error }).payload?.message ?? ''
    const url = message.match(/(?:https?:\/\/\S+|\/assets\/\S+)\.(?:js|css)/)?.[0]
    if (!url) return

    void diagnoseAndRecover(url)
  })
}

/**
 * 頁面檔案載入失敗時 vue-router 只會中止導航，畫面毫無反應、使用者只能對著按鈕猜
 * 註冊錯誤處理讓失敗至少看得見（vue-router 仍會 reject，原始錯誤由 Sentry 全域處理器回報）
 */
export function registerRouteLoadErrorAlert(router: Router): void {
  router.onError((error) => {
    console.error('Route load failed:', error)

    // chunk 失敗已由探測流程接手（可能自動重整），不重複跳提示
    if (handling) return

    alert(i18n.global.t('common.pageLoadFailed'))
  })
}
